import {
  BoundNumberField,
  Button,
  column,
  GridCellContent,
  GridColumn,
  GridTable,
  ScrollableContent,
  simpleDataRows,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { PageHeader } from "src/routes/layout/PageHeader";
import { queryResult } from "src/utils/queryResult";
import {
  AddJobsInput,
  CheckoutControls_LotPartitionFragment,
  Exact,
  Maybe,
  QueueJobsMutation,
  useCheckoutControlsQuery,
  useQueueJobsMutation,
} from "src/generated/graphql-types";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { priceCellContent } from "src/components";
import { MutationFunctionOptions, DefaultContext, ApolloCache, FetchResult } from "@apollo/client";
import { formatCentsToPrice } from "src/utils";
import { getBlueprintBaseUrl, getSalesforceBaseUrl, getUnderwritingBaseUrl } from "src/context";

export function CheckoutControlsPage() {
  const query = useCheckoutControlsQuery({ variables: {} });

  return queryResult(query, {
    data: (data) => {
      const title = `Checkout Controls`;
      return (
        <>
          <PageHeader title={title} />
          <ScrollableContent>
            <CheckoutControlsTable lotPartitions={data.lotPartitions} />
          </ScrollableContent>
        </>
      );
    },
  });
}

const blueprintBaseUrl = getBlueprintBaseUrl();
const underwritingBaseUrl = getUnderwritingBaseUrl();
const salesforceBaseUrl = getSalesforceBaseUrl();

function CheckoutControlsTable({ lotPartitions }: { lotPartitions: CheckoutControls_LotPartitionFragment[] }) {
  const formConfig: ObjectConfig<FormInput> = useMemo(
    () => ({
      id: { type: "value" },
      lotPartitions: {
        type: "list",
        config: checkoutControlsLotPartitionConfig,
      },
    }),
    [],
  );
  const formState = useFormState({
    config: formConfig,
    init: {
      input: lotPartitions,
      map: mapToForm,
    },
  });

  const [queueJobs] = useQueueJobsMutation();

  return (
    <GridTable
      id="checkoutControlsLotPartitionsTable"
      rowStyles={{ header: {}, data: {} }}
      columns={createColumns(formState, queueJobs)}
      rows={simpleDataRows(lotPartitions)}
      fallbackMessage="Published Projects in Checkout will show here."
      sorting={{ on: "client" }}
    />
  );
}

type Row = SimpleHeaderAndData<CheckoutControls_LotPartitionFragment>;

function createColumns(
  formState: ObjectState<FormInput>,
  queueJobs: {
    (
      options?:
        | MutationFunctionOptions<QueueJobsMutation, Exact<{ input: AddJobsInput }>, DefaultContext, ApolloCache<any>>
        | undefined,
    ): Promise<FetchResult<QueueJobsMutation>>;
    (arg0: {
      variables: {
        input: { name: string; payloads: { creditFacilityInvoiceId: any }[] };
      };
    }): any;
  },
): GridColumn<Row>[] {
  const idColumn = column<Row>({
    header: () => "Partition ID",
    data: ({ id }) => id,
    w: "100px",
  });

  const addressColumn = column<Row>({
    header: () => "Investment Address",
    data: ({ lot }) => lot?.address?.street1 ?? "-",
    w: "200px",
  });

  const blueprintIdColumn = column<Row>({
    header: () => "Blueprint ID",
    data: ({ blueprintProjectId }) => {
      return {
        alignment: "left",
        content: (
          <Button
            label={blueprintProjectId}
            variant="tertiary"
            onClick={() => {
              // Open the Blueprint contracts list (with empty filters to get them all) in a new tab
              window.open(
                `${blueprintBaseUrl}/projects/${blueprintProjectId}/lot-summary`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
          />
        ),
        value: blueprintProjectId,
      };
    },
    w: "100px",
  });

  const shownInCheckoutColumn = column<Row>({
    header: () => "Shown In Checkout",
    data: ({ showInCheckout }) => (showInCheckout ? "Yes" : "No"),
    w: "200px",
  });

  const underwrittenPriceColumn = column<Row>({
    header: () => "UW Sales Price (Spot)",
    data: ({ underwrittenSalesPriceWithoutAppreciationInCents, lot }) => {
      return {
        alignment: "left",
        content: (
          <Button
            label={formatCentsToPrice(underwrittenSalesPriceWithoutAppreciationInCents)}
            variant="tertiary"
            onClick={() => {
              // Open the Underwriting Clear to Close report from which this came
              window.open(
                `${underwritingBaseUrl}/cma/${lot.dpid}/versions/${lot.clearToCloseUwReportId}/estimate`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
            disabled={!lot.clearToCloseUwReportId || !lot.dpid}
          />
        ),
        value: underwrittenSalesPriceWithoutAppreciationInCents,
      };
    },
    w: "150px",
  });

  const valuationPriceColumn = column<Row>({
    header: () => "Valuation Sales Price (Spot)",
    data: ({ salesPriceAtValuationDateInCents, lot, valuationReportId }) => {
      return {
        alignment: "left",
        content: (
          <Button
            label={formatCentsToPrice(salesPriceAtValuationDateInCents)}
            variant="tertiary"
            onClick={() => {
              // Open the most recent Valuation report from which this came
              window.open(
                `${underwritingBaseUrl}/cma/${lot.dpid}/versions/${valuationReportId}/estimate`,
                "_blank",
                "noopener,noreferrer",
              );
            }}
            disabled={!valuationReportId || !lot.dpid}
          />
        ),
        value: salesPriceAtValuationDateInCents,
      };
    },
    w: "150px",
  });

  const listPriceColumn = column<Row>({
    header: () => "List Price (SFDC)",
    data: ({ salesforceHomeListingId, listPriceInCents }) => {
      const label = listPriceInCents ? formatCentsToPrice(listPriceInCents) : "N/A";
      return {
        alignment: "left",
        content: (
          <Button
            label={label}
            variant="tertiary"
            onClick={
              salesforceHomeListingId
                ? () =>
                    window.open(
                      `${salesforceBaseUrl}/Home_Listing__c/${salesforceHomeListingId}/view`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                : () => {}
            }
            disabled={!salesforceHomeListingId}
          />
        ),
        value: listPriceInCents,
      };
    },
    w: "150px",
  });

  const basePriceColumn = column<Row>({
    header: () => "Current Checkout Base Price",
    data: ({ basePriceInCents }) => amountGridCellContent(basePriceInCents),
    w: "150px",
  });

  const proposedBasePriceColumn = column<Row>({
    header: () => "Proposed Base Price",
    data: ({ proposedBasePriceInCents }) => amountGridCellContent(proposedBasePriceInCents),
    w: "100px",
  });

  const priceToPublishColumn = column<Row>({
    header: () => "Price to Publish",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        value: () => formStateField!.priceToPublishInCents.value,
        content: () => <BoundNumberField type="cents" field={formStateField!.priceToPublishInCents} />,
        tooltip: "The price that we will publish to Checkout once the button is pushed",
      };
    },
    w: "200px",
  });

  const publishPriceColumn = column<Row>({
    header: () => "Publish Price",
    data: (row) => {
      const formStateField = formState.lotPartitions.rows.find((lp) => lp.id.value === row.id);
      return {
        alignment: "left",
        content: (
          <Button
            label={"Publish"}
            variant="primary"
            onClick={async () => {
              await queueJobs({
                variables: {
                  input: {
                    name: "updateBlueprintCheckoutBasePrice",
                    payloads: [
                      {
                        lotPartitionId: row.id,
                        priceToPublishInCents: formStateField!.priceToPublishInCents.value,
                      },
                    ],
                  },
                },
              });
            }}
            disabled={
              formStateField!.priceToPublishInCents.value === row.basePriceInCents ||
              !formStateField!.priceToPublishInCents.value
            }
            tooltip={"Queues the Price to Publish to Checkout - refresh to see update"}
          />
        ),
        value: null,
      };
    },
    w: "150px",
  });

  return [
    addressColumn,
    idColumn,
    blueprintIdColumn,
    shownInCheckoutColumn,
    underwrittenPriceColumn,
    valuationPriceColumn,
    listPriceColumn,
    basePriceColumn,
    proposedBasePriceColumn,
    priceToPublishColumn,
    publishPriceColumn,
  ];
}

function amountGridCellContent(amount: number | null | undefined): GridCellContent {
  return {
    alignment: "left",
    content: amount ? priceCellContent(amount) : "-",
    value: amount,
  };
}

type FormInput = {
  lotPartitions: Maybe<FormValues[]>;
};

function mapToForm(lotPartitions: CheckoutControls_LotPartitionFragment[]): FormInput {
  return {
    lotPartitions: mapLotPartitionsToForm(lotPartitions),
  };
}

function mapLotPartitionsToForm(lotPartitions: CheckoutControls_LotPartitionFragment[]): Maybe<FormValues[]> {
  return lotPartitions?.map((lp) => {
    return {
      id: lp.id,
      priceToPublishInCents: lp.proposedBasePriceInCents ?? lp.basePriceInCents,
    };
  });
}

type FormValues = {
  id: string;
  priceToPublishInCents: number | null | undefined;
};

export const checkoutControlsLotPartitionConfig: ObjectConfig<FormValues> = {
  id: { type: "value" },
  priceToPublishInCents: { type: "value" },
};
